import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import { Spinner } from '../components/Spinner'
import { LockIcon } from '../components/icons'
import './LoginPage.css'

/**
 * Where an invitation or a password-reset link lands.
 *
 * Both mails carry a token in the URL; the Supabase client trades it for a
 * session before this screen renders (detectSessionInUrl), so by the time
 * anyone sees the form they are already signed in, they just have no
 * password yet. Setting one is the whole job.
 *
 * Reached without a token, a bookmarked copy, a link opened twice, there is
 * no session to update, and the screen says so rather than presenting a form
 * that could only fail.
 */
export function SetPasswordPage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [invited, setInvited] = useState(false)

  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!supabase) {
      setChecking(false)
      return
    }
    // The client consumes the token asynchronously, so the session is read
    // here rather than in a first render that would always find none.
    supabase.auth.getSession().then(({ data }) => {
      setInvited(Boolean(data.session))
      setChecking(false)
    })
  }, [])

  const problem = password && password.length < 8
    ? 'Şifre en az 8 karakter olmalı.'
    : password2 && password !== password2
      ? 'Şifreler birbirini tutmuyor.'
      : null

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!supabase || problem || !password || password !== password2) return

    setBusy(true)
    setError(null)
    try {
      const { error: err } = await supabase.auth.updateUser({ password })
      if (err) throw new Error(err.message)
      // Straight to the till: the session is already live, and sending
      // someone to a sign-in screen to type what they just chose reads as
      // though the password did not take.
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Şifre belirlenemedi.')
      setBusy(false)
    }
  }

  if (checking) {
    return <Spinner label="Yükleniyor…" full />
  }

  if (!invited) {
    return (
      <div className="login login--solo">
        <div className="login__card">
          <h1>Bağlantı geçersiz</h1>
          <p className="login__lead">
            Davet bağlantısının süresi dolmuş ya da daha önce kullanılmış.
            Yeni bir bağlantı isteyin.
          </p>
          <button
            type="button"
            className="btn btn--primary btn--lg"
            onClick={() => navigate('/giris', { replace: true })}
          >
            Girişe dön
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login login--solo">
      <form className="login__card" onSubmit={onSubmit} autoComplete="off">
        <h1>Şifrenizi belirleyin</h1>
        <p className="login__lead">
          Bundan sonra e-postanız ve bu şifreyle giriş yapacaksınız.
        </p>

        <div className="fld">
          <label htmlFor="pw">Şifre</label>
          <div className="fld__with-icon">
            <span className="fld__icon" aria-hidden="true"><LockIcon /></span>
            <input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="fld">
          <label htmlFor="pw2">Şifre (tekrar)</label>
          <div className="fld__with-icon">
            <span className="fld__icon" aria-hidden="true"><LockIcon /></span>
            <input
              id="pw2"
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className="btn btn--primary btn--lg"
          disabled={busy || Boolean(problem) || !password || password !== password2}
        >
          {busy ? 'Kaydediliyor…' : 'Kaydet'}
        </button>

        {(problem || error) && (
          <div className="login__err" role="alert">
            <div className="login__err-title">{problem ?? error}</div>
          </div>
        )}
      </form>
    </div>
  )
}
